import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthenticatedUser, useFeature } from '@treams/treams-hooks';
import { AddIcon, CalendarIcon, DraftsIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Button, CardList, Jumbo } from '@treams/treams-ui';

import NoteCard from './NoteCard';
import RichNoteCard from './RichNoteCard';
import OneOnOnesSummary from './one-on-ones/OneOnOnesSummary';
import { oneOnOneNoteGroupOptions, sortOneOnOneNotes } from './oneOnOneNoteGroups';

export default function PeerSharedNotes(props: { userID: string }) {
	const { t } = useTranslation();

	const viewer = useAuthenticatedUser();
	const viewerID = viewer.id;
	const hasOneOnOnes = useFeature(graphql.FeatureName.OneOnOnes);

	const query = graphql.useNotesQuery({
		variables: {
			first: 10,
			filters: { subjectID: props.userID, isArchived: false },
		},
	});

	const subjectQuery = graphql.useUserQuery({ variables: { id: props.userID } });

	const subjectTitle =
		props.userID === viewer.id
			? t('oneOnOnes.index.titleAboutYou')
			: subjectQuery.data
				? t('oneOnOnes.index.titleAboutSubject', {
						subject: subjectQuery.data?.user?.firstName ?? '',
					})
				: '';

	const newNoteLink = useMemo(() => {
		const newNotePath = hasOneOnOnes ? `/notes/new/subject/${props.userID}` : '/notes/new';
		return `${newNotePath}?userID=${props.userID}`;
	}, [hasOneOnOnes, props.userID]);

	const linkPrefix = `/users/${props.userID}`;

	return (
		<>
			<Jumbo
				title={hasOneOnOnes ? subjectTitle : t('peer.notes.shared.title')}
				description={hasOneOnOnes ? undefined : t('peer.notes.shared.description')}
				button={
					<Button
						icon={<AddIcon />}
						title={hasOneOnOnes ? t('oneOnOnes.index.new') : t('peer.notes.index.new')}
						link={newNoteLink}
					/>
				}
				footer={
					hasOneOnOnes ? (
						<OneOnOnesSummary subjectID={props.userID} noteCount={query.data?.notes.edges.length} />
					) : undefined
				}
			/>

			<CardList
				translationName={hasOneOnOnes ? 'oneOnOnes.index' : 'notes.index'}
				query={query}
				path="notes"
				icon={hasOneOnOnes ? <CalendarIcon /> : <DraftsIcon />}
				renderCard={(item: graphql.Note) =>
					hasOneOnOnes ? (
						<RichNoteCard linkPrefix={linkPrefix} userID={viewerID} note={item} />
					) : (
						<NoteCard linkPrefix={linkPrefix} userID={viewerID} note={item} />
					)
				}
				renderSkeleton={() => (hasOneOnOnes ? <RichNoteCard.Skeleton /> : <NoteCard.Skeleton />)}
				groupOptions={hasOneOnOnes ? oneOnOneNoteGroupOptions(t) : undefined}
				sortItems={hasOneOnOnes ? sortOneOnOneNotes : undefined}
			/>
		</>
	);
}
